import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowRight, MapPin, Phone, Check, Plus, Star } from "lucide-react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { localServiceBySlug, localServices } from "@/data/local-services";
import { bundeslaender as allBl, locationsByBundesland, wienBezirke, staedte as allStaedte } from "@/data/locations";
import { isFocusLocation } from "@/data/focus";
const bundeslaender = allBl.filter(isFocusLocation);
const staedte = allStaedte.filter(isFocusLocation);
import { Testimonials } from "@/components/site/Testimonials";
import { serviceFaqs, serviceWhyUs } from "@/data/service-content";

export const Route = createFileRoute("/leistungen_/$service")({
  loader: ({ params }) => {
    const service = localServiceBySlug[params.service];
    if (!service) throw notFound();
    return { service };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return { meta: [] };
    const { service } = loaderData;
    const url = `https://schober-transportservice.at/leistungen/${service.slug}`;
    const faqsForService = serviceFaqs[service.slug] ?? [];
    return {
      meta: [
        { title: `${service.titleLong} – Angebot | Schober`.slice(0, 58) },
        {
          name: "description",
          content: `${service.shortDescription} Angebot nach kostenloser Besichtigung, versichert bis 25.000 €, Termin oft in 48 h. Schober Transportservice – Wien, NÖ, Burgenland & ganz Österreich. ☎ +43 680 230 87 00.`,
        },
        { name: "keywords", content: `${service.slug}, ${service.title}, ${service.titleLong}, Schober Transportservice` },
        { property: "og:title", content: `${service.titleLong} – Schober Transportservice` },
        { property: "og:description", content: service.shortDescription },
        { property: "og:image", content: "https://schober-transportservice.at" + service.image },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: service.titleLong,
            description: service.shortDescription,
            provider: {
              "@type": "LocalBusiness",
              name: "Schober Transportservice",
              telephone: "+43 680 230 87 00",
              areaServed: ["Wien", "Niederösterreich", "Burgenland", "Österreich"],
            },
            areaServed: { "@type": "Country", name: "Österreich" },
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: "5.0",
              reviewCount: "16",
            },
          }),
        },
        ...(faqsForService.length > 0
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  mainEntity: faqsForService.map((f) => ({
                    "@type": "Question",
                    name: f.q,
                    acceptedAnswer: { "@type": "Answer", text: f.a },
                  })),
                }),
              },
            ]
          : []),
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://schober-transportservice.at/" },
              { "@type": "ListItem", position: 2, name: "Leistungen", item: "https://schober-transportservice.at/leistungen" },
              { "@type": "ListItem", position: 3, name: service.title, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: ServiceOverviewPage,
  notFoundComponent: () => (
    <div className="container mx-auto max-w-3xl px-4 py-20 text-center">
      <h1 className="font-display text-3xl font-bold">Service nicht gefunden</h1>
      <Link to="/leistungen" className="mt-4 inline-block text-brand-orange underline">
        Zur Leistungsübersicht
      </Link>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="container mx-auto max-w-3xl px-4 py-20 text-center">
      <h1 className="font-display text-3xl font-bold">Fehler</h1>
      <p className="mt-4">{error instanceof Error ? error.message : String(error)}</p>
    </div>
  ),
});

function ServiceOverviewPage() {
  const { service } = Route.useLoaderData();

  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <img
          src={service.image}
          alt={`${service.titleLong} in Österreich`}
          width={1600}
          height={900}
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-brand-navy/70" aria-hidden />
        <div className="relative z-10 container mx-auto max-w-6xl px-4 py-20 md:py-28 text-white">
          <nav aria-label="Breadcrumb" className="mb-4 text-sm text-white/80">
            <Link to="/" className="hover:text-brand-orange-on-dark">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/leistungen" className="hover:text-brand-orange-on-dark">Leistungen</Link>
            <span className="mx-2">/</span>
            <span className="text-white">{service.title}</span>
          </nav>
          <h1 className="font-display text-4xl font-extrabold uppercase tracking-tight md:text-6xl">
            {service.titleLong}
          </h1>
          <p className="mt-6 max-w-3xl text-lg text-white/90 md:text-xl">{service.shortDescription}</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a
              href="tel:00436802308700"
              className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-6 py-3 font-bold text-brand-orange-foreground transition hover:scale-105"
            >
              <Phone className="h-4 w-4" /> +43 680 230 87 00
            </a>
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 rounded-full border-2 border-white px-6 py-3 font-bold text-white transition hover:bg-white hover:text-brand-navy"
            >
              Kostenloses Angebot <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="container mx-auto max-w-6xl px-4 py-16">
        <div className="grid gap-12 md:grid-cols-2">
          <div>
            <h2 className="font-display text-3xl font-bold text-brand-navy md:text-4xl">
              {service.titleLong} – in ganz Österreich
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-foreground/90">{service.intro}</p>
          </div>
          <ul className="grid gap-3">
            {service.bullets.map((b: string) => (
              <li key={b} className="flex gap-3">
                <Check className="mt-1 h-5 w-5 shrink-0 text-brand-orange" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Unser Ablauf – 4 Schritte */}
      {service.slug === "umzug" && (
        <section className="bg-muted py-12 md:py-16">
          <div className="container mx-auto max-w-6xl px-4">
            <h2 className="font-display text-3xl font-bold text-brand-navy">
              Fernumzug: von Wien nach Salzburg oder von Wiener Neustadt nach Kärnten
            </h2>
            <p className="mt-5 max-w-3xl leading-relaxed text-muted-foreground">
              Ein Studienplatz, ein Arbeitsplatz oder eine neue Wohnung liegt nicht immer in der Nähe.
              Wir unterstützen Sie auch bei längeren Umzügen mit Start oder Ziel in Wien,
              Niederösterreich oder Burgenland. Dazu zählen etwa Wien–Salzburg,
              Wiener Neustadt–Kärnten oder Baden–Tirol – ebenso in die umgekehrte Richtung.
            </p>
            <h3 className="mt-8 font-display text-xl font-bold text-brand-navy">
              Zwei Adressen, ein abgestimmter Umzugsplan
            </h3>
            <p className="mt-3 max-w-3xl leading-relaxed text-muted-foreground">
              Für die Planung zählen die Bedingungen an beiden Wohnungen: Tragewege, Stockwerke,
              Liftgröße, Zufahrt und Platz zum Be- und Entladen. Teilen Sie uns außerdem die Menge
              Ihrer Möbel und Kartons sowie mögliche Übergabetermine mit. So lassen sich Fahrt,
              Beladung und Ankunft sinnvoll abstimmen; Ihr Angebot wird individuell kalkuliert.
            </p>
            <p className="mt-5 max-w-3xl leading-relaxed text-muted-foreground">
              Unser regionaler Schwerpunkt bleibt rund um{" "}
              <Link to="/leistungen/$service/$ort" params={{ service: "umzug", ort: "wien" }} className="font-semibold text-brand-navy hover:underline">Wien</Link>,{" "}
              <Link to="/leistungen/$service/$ort" params={{ service: "umzug", ort: "wiener-neustadt" }} className="font-semibold text-brand-navy hover:underline">Wiener Neustadt</Link>,{" "}
              <Link to="/leistungen/$service/$ort" params={{ service: "umzug", ort: "baden" }} className="font-semibold text-brand-navy hover:underline">Baden bei Wien</Link> und{" "}
              <Link to="/leistungen/$service/$ort" params={{ service: "umzug", ort: "neunkirchen" }} className="font-semibold text-brand-navy hover:underline">Neunkirchen</Link>.
              Von dort begleiten wir Sie auch über Bundeslandgrenzen hinweg.
            </p>
            <Link to="/kontakt" className="mt-6 inline-flex items-center gap-2 font-semibold text-brand-navy hover:underline">
              Fernumzug anfragen <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
      )}

      {/* Unser Ablauf – 4 Schritte */}
      <section className="bg-brand-navy py-16 text-white">
        <div className="container mx-auto max-w-6xl px-4">
          <h2 className="font-display text-3xl font-bold md:text-4xl">
            So läuft Ihre {service.title} ab
          </h2>
          <p className="mt-3 max-w-2xl text-white/80">
            Transparent, strukturiert und ohne Überraschungen – vom ersten Anruf bis zur
            Schlüsselübergabe übernehmen wir alle Schritte für Sie.
          </p>
          <ol className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {service.process.map((step: { title: string; text: string }, i: number) => (
              <li
                key={step.title}
                className="relative rounded-lg bg-white/5 p-6 ring-1 ring-white/10"
              >
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-brand-orange font-display text-lg font-extrabold text-brand-orange-foreground">
                    {i + 1}
                  </span>
                  <h3 className="font-display text-lg font-bold">{step.title}</h3>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-white/85">{step.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Warum Schober Transportservice */}
      <section className="container mx-auto max-w-6xl px-4 py-16">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.4fr] lg:items-start">
          <div>
            <p className="text-sm font-bold uppercase tracking-wider text-brand-orange">
              Warum Schober Transportservice
            </p>
            <h2 className="mt-3 font-display text-3xl font-bold text-brand-navy md:text-4xl">
              Erfahrung, Diskretion und ein echter Angebot
            </h2>
            <p className="mt-6 leading-relaxed text-foreground/85">
              Gegründet 2023 von Patric Schober – aktivem Polizisten in Wien Favoriten – steht
              Schober Transportservice für das, was in unserer Branche zählt: absolute Verlässlichkeit,
              Diskretion und Respekt vor fremdem Eigentum. Wir arbeiten mit eigenem geschultem Team und
              eigenem Fuhrpark, ohne Subunternehmen, und liefern den Preis, den wir vorab kalkuliert haben.
              Punkt.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4 text-sm">
              <div className="flex items-center gap-1.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-brand-orange text-brand-orange" />
                ))}
                <span className="ml-2 font-semibold text-brand-navy">5,0 / 5</span>
                <span className="text-muted-foreground">· 16 Bewertungen</span>
              </div>
            </div>
          </div>
          <ul className="grid gap-3 sm:grid-cols-2">
            {(serviceWhyUs[service.slug] ?? []).map((reason) => (
              <li
                key={reason}
                className="flex gap-3 rounded-lg border border-border bg-muted/40 p-4"
              >
                <Check className="mt-0.5 h-5 w-5 shrink-0 text-brand-orange" />
                <span className="text-sm leading-relaxed">{reason}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Service-spezifische FAQ */}
      {(serviceFaqs[service.slug] ?? []).length > 0 && (
        <section className="bg-muted py-16">
          <div className="container mx-auto max-w-4xl px-4">
            <p className="text-sm font-bold uppercase tracking-wider text-brand-orange">
              Häufige Fragen
            </p>
            <h2 className="mt-3 font-display text-3xl font-bold text-brand-navy md:text-4xl">
              Was Kunden zur {service.title} oft fragen
            </h2>
            <AccordionPrimitive.Root type="single" collapsible className="mt-8">
              {(serviceFaqs[service.slug] ?? []).map((f, i) => (
                <AccordionPrimitive.Item
                  key={i}
                  value={`svc-faq-${i}`}
                  className="border-b border-border"
                >
                  <AccordionPrimitive.Header className="flex">
                    <AccordionPrimitive.Trigger className="group flex flex-1 items-center gap-4 py-4 text-left font-semibold text-brand-navy transition-colors hover:text-brand-orange [&[data-state=open]>span>svg]:rotate-45">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center bg-brand-navy text-white">
                        <Plus className="h-4 w-4 transition-transform duration-200" />
                      </span>
                      <span>{f.q}</span>
                    </AccordionPrimitive.Trigger>
                  </AccordionPrimitive.Header>
                  <AccordionPrimitive.Content className="overflow-hidden text-sm data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                    <div className="pb-5 pl-10 pr-2 leading-relaxed text-muted-foreground">
                      {f.a}
                    </div>
                  </AccordionPrimitive.Content>
                </AccordionPrimitive.Item>
              ))}
            </AccordionPrimitive.Root>
            <p className="mt-8 text-sm text-muted-foreground">
              Weitere Fragen?{" "}
              <Link to="/faq" className="font-semibold text-brand-orange hover:underline">
                Vollständige FAQ ansehen
              </Link>{" "}
              oder{" "}
              <Link to="/kontakt" className="font-semibold text-brand-orange hover:underline">
                direkt nachfragen
              </Link>
              .
            </p>
          </div>
        </section>
      )}



      {/* Standorte nach Bundesland */}
      <section className="bg-muted py-16">
        <div className="container mx-auto max-w-6xl px-4">
          <h2 className="font-display text-3xl font-bold text-brand-navy md:text-4xl">
            {service.title} in Ihrer Region
          </h2>
          <p className="mt-3 max-w-3xl text-foreground/80">
            Schober Transportservice ist in Wien, Niederösterreich und dem Burgenland für Sie da – von Wiener Neustadt, Baden und Mödling bis Eisenstadt und Mattersburg. Wählen Sie Ihren Ort –
            wir haben für jeden Standort eine eigene Spezialseite mit allen Details.
          </p>

          {/* Bundesländer + Hauptstädte */}
          <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {bundeslaender.map((bl) => {
              const cities = locationsByBundesland(bl.bundeslandSlug);
              return (
                <div key={bl.slug} className="rounded-lg bg-background p-6 shadow-sm">
                  <h3 className="font-display text-xl font-bold text-brand-navy">
                    <MapPin className="inline h-5 w-5 text-brand-orange" /> {bl.name}
                  </h3>
                  <Link
                    to="/leistungen/$service/$ort"
                    params={{ service: service.slug, ort: bl.slug }}
                    className="mt-2 inline-block text-sm font-semibold text-brand-orange hover:underline"
                  >
                    → {service.title} in ganz {bl.name}
                  </Link>
                  {cities.length > 0 && (
                    <ul className="mt-3 flex flex-wrap gap-1.5">
                      {cities.slice(0, 12).map((c) => (
                        <li key={c.slug}>
                          <Link
                            to="/leistungen/$service/$ort"
                            params={{ service: service.slug, ort: c.slug }}
                            className="inline-block rounded-full bg-muted px-3 py-1 text-xs transition hover:bg-brand-orange hover:text-brand-orange-foreground"
                          >
                            {c.name}
                          </Link>
                        </li>
                      ))}
                      {cities.length > 12 && (
                        <li className="px-3 py-1 text-xs text-muted-foreground">
                          + {cities.length - 12} weitere
                        </li>
                      )}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>

          {/* Wiener Bezirke */}
          <div className="mt-10 rounded-lg bg-background p-6 shadow-sm">
            <h3 className="font-display text-xl font-bold text-brand-navy">
              <MapPin className="inline h-5 w-5 text-brand-orange" /> Alle 23 Wiener Gemeindebezirke
            </h3>
            <ul className="mt-4 grid gap-1.5 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
              {wienBezirke.map((b) => (
                <li key={b.slug}>
                  <Link
                    to="/leistungen/$service/$ort"
                    params={{ service: service.slug, ort: b.slug }}
                    className="block rounded px-2 py-1 text-sm transition hover:bg-brand-orange hover:text-brand-orange-foreground"
                  >
                    {b.plz} {b.name.replace(/^Wien \d+\. Bezirk – /, "")}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Alphabetische Städteliste */}
          <details className="mt-8 rounded-lg bg-background p-6 shadow-sm">
            <summary className="cursor-pointer font-display text-lg font-bold text-brand-navy">
              Alle {staedte.length} Städte alphabetisch anzeigen
            </summary>
            <ul className="mt-4 grid gap-1.5 grid-cols-2 sm:grid-cols-3 md:grid-cols-4">
              {[...staedte]
                .sort((a, b) => a.name.localeCompare(b.name, "de"))
                .map((c) => (
                  <li key={c.slug}>
                    <Link
                      to="/leistungen/$service/$ort"
                      params={{ service: service.slug, ort: c.slug }}
                      className="block rounded px-2 py-1 text-sm transition hover:bg-brand-orange hover:text-brand-orange-foreground"
                    >
                      {c.name}
                    </Link>
                  </li>
                ))}
            </ul>
          </details>
        </div>
      </section>

      {/* Entrümpelung Quick-Links – Prio-Städte */}
      {service.slug !== "entruempelung" && (
        <section className="container mx-auto max-w-6xl px-4 py-16">
          <p className="text-sm font-bold uppercase tracking-wider text-brand-orange">
            Auch gefragt
          </p>
          <h2 className="mt-3 font-display text-2xl font-bold text-brand-navy md:text-3xl">
            Entrümpelung in Ihrer Stadt – Angebot in 48 h
          </h2>
          <p className="mt-3 max-w-3xl text-foreground/80">
            Neben {service.title} übernehmen wir auch die komplette Entrümpelung – von der
            Wohnung bis zum Keller, inklusive Abtransport des Räumguts und besenreiner Übergabe.
          </p>
          <ul className="mt-6 grid gap-2 grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
            {[
              { ort: "wien", name: "Wien" },
              { ort: "wiener-neustadt", name: "Wiener Neustadt" },
              { ort: "st-poelten", name: "St. Pölten" },
              { ort: "baden", name: "Baden" },
              { ort: "eisenstadt", name: "Eisenstadt" },
              { ort: "mattersburg", name: "Mattersburg" },
            ].map((c) => (
              <li key={c.ort}>
                <Link
                  to="/leistungen/$service/$ort"
                  params={{ service: "entruempelung", ort: c.ort }}
                  className="flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 text-sm font-semibold text-brand-navy transition hover:border-brand-orange hover:bg-brand-orange hover:text-brand-orange-foreground"
                >
                  <MapPin className="h-4 w-4 shrink-0 text-brand-orange" />
                  Entrümpelung {c.name}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Andere Services */}
      <section className="container mx-auto max-w-6xl px-4 py-16">
        <h2 className="font-display text-2xl font-bold text-brand-navy md:text-3xl">Weitere Leistungen</h2>
        <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {localServices
            .filter((s) => s.slug !== service.slug)
            .map((s) => (
              <Link
                key={s.slug}
                to="/leistungen/$service"
                params={{ service: s.slug }}
                className="group block rounded-lg border border-border p-5 transition hover:border-brand-orange hover:shadow-md"
              >
                <h3 className="font-display text-lg font-bold text-brand-navy group-hover:text-brand-orange">
                  {s.title}
                </h3>
                <p className="mt-2 text-sm text-foreground/75 line-clamp-2">{s.shortDescription}</p>
              </Link>
            ))}
        </div>
      </section>
      <Testimonials />
    </>
  );
}
